import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import ReCAPTCHA from "react-google-recaptcha";
import ReactGA from 'react-ga4';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const onRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!formData.name || !formData.email || !formData.company) {
      setError('Please fill in all required fields.');
      return;
    }

    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA verification.');
      return;
    }

    setIsSending(true);

    try {
      const response = await fetch('/api/sendEmail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, recaptchaToken }),
      });

      if (response.ok) {
        ReactGA.event({
          category: 'Lead',
          action: 'contact_form_submit_success',
          label: 'Contact Form',
        });
        setSubmitted(true);
      } else {
        // Try to parse JSON, fallback to text if it fails
        let errorMessage = 'Something went wrong. Please try again.';
        try {
            const text = await response.text(); // Read text once
            try {
                const errorData = JSON.parse(text); // Try to parse as JSON
                if (errorData && errorData.body) errorMessage = errorData.body;
            } catch {
                if (text) errorMessage = text; // If JSON parse fails, use text
            }
        } catch (e) {
             console.error("Error reading response:", e);
        }
        setError(errorMessage);
      }
    } catch (err) {
      console.error("Submission error:", err); // Log the actual error to console
      setError('Failed to send message. Please check your connection and try again.');
    } finally {
      setIsSending(false);
    }
  };

  if (submitted) {
    return (
      <div className="page-wrapper">
        <Helmet>
          <title>Thank You | Clariti AI</title>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
        <section className="contact-hero">
          <div className="container">
            <h1>Thank you!</h1>
            <p>We have received your message and will be in touch shortly.</p>
          </div>
        </section>
        <section className="coming-soon-section">
          <div className="container">
            <div className="coming-soon-box">
              <p>We'll be in touch within 24 hours to schedule your demo.</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <Helmet>
        <title>Contact Clariti AI | Book a Demo</title>
        <meta name="description" content="Get in touch with Clariti AI to book a demo or learn more about our pricing optimisation platform for banks." />
        <link rel="canonical" href="https://www.clariti.ai/contact" />
        <meta property="og:title" content="Contact Clariti AI | Book a Demo" />
        <meta property="og:description" content="Book a demo and see how Clariti AI helps banks optimise mortgage and savings pricing strategy." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.clariti.ai/contact" />
        <meta name="twitter:title" content="Contact Clariti AI | Book a Demo" />
        <meta name="twitter:description" content="Speak with Clariti AI about pricing optimisation software for your banking team." />
      </Helmet>
      {/* Hero */}
      <section className="contact-hero">
        <div className="container">
          <h1>Let's talk</h1>
          <p>Book a demo or get in touch — we'd love to hear from you.</p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="contact-section">
        <div className="container">
          <div className="contact-grid">
            <form className="contact-form" onSubmit={handleSubmit}>
              {error && (
                <div className="form-error">
                  {error}
                </div>
              )}
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="company">Company *</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">Message (optional)</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us a bit about what you're looking for..."
                />
              </div>

              <div className="form-group">
                {process.env.REACT_APP_RECAPTCHA_SITE_KEY ? (
                  <ReCAPTCHA
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={onRecaptchaChange}
                  />
                ) : (
                  <div style={{ color: 'red', border: '1px solid red', padding: '10px' }}>
                    Error: reCAPTCHA Site Key is missing. Please add REACT_APP_RECAPTCHA_SITE_KEY to your .env file.
                  </div>
                )}
              </div>

              <button type="submit" className="btn btn-primary" disabled={isSending}>
                {isSending ? 'Sending...' : 'Send message'}
              </button>
            </form>

            <div className="contact-info">
              <h3>What happens next?</h3>
              <p>
                One of our team will be in touch within 24 hours to schedule a call. 
                In the demo, we'll walk you through the platform, discuss your specific 
                challenges, and answer any questions.
              </p>

              <div className="contact-detail">
                <h4>Prefer email?</h4>
                <p>
                  <a href="mailto:info@clariti.ai">info@clariti.ai</a>
                </p>
              </div>

              <div className="contact-detail">
                <h4>Follow us</h4>
                <p>
                  <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer">
                    LinkedIn
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
